/**
 * Membership Stats month drill-down (WSRADM-0021).
 *
 * Tab counts come from the month-header call. Band options come from a
 * `months?from=&to=&size=1` envelope (`durationColumns`) — never from the
 * member rows on screen. `state` is always sent on the members GET.
 */
import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchMembershipMembers,
  fetchMembershipMonth,
  fetchMembershipMonths,
  rebuildReports,
} from '@/api/reports/reportsApi';
import type { MemberState, MembershipMember } from '@/api/reports/types';
import { currentMonthKey, previousMonth } from '@/api/reports/format';
import { useAuth } from '@/auth/AuthContext';
import { canRebuildReports } from '@/auth/reportAccess';
import { ReportFreshness } from '@/components/ReportFreshness';
import { RosterTable, type RosterColumn } from '@/components/RosterTable';
import { EM_DASH } from '@/components/rosterFormat';
import { ErrorDetails } from '@/components/ErrorDetails';
import { useCopy } from '@/hooks/useCopy';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useResource } from '@/hooks/useResource';

const PAGE_SIZE_DEFAULT = 20;

const MEMBER_SORT_ALLOWLIST = new Set([
  'purchaseCount',
  'memberName',
  'latestPurchaseAt',
  'latestProductName',
]);

type MonthTab = 'lost' | 'lapsed' | 'existing' | 'new' | 'returning';

const TAB_STATE: Record<MonthTab, MemberState> = {
  lost: 'LOST',
  lapsed: 'LAPSED',
  existing: 'EXISTING',
  new: 'NEW',
  returning: 'RETURNING',
};

function parseTab(raw: string | null): MonthTab {
  if (raw === 'lapsed' || raw === 'existing' || raw === 'new' || raw === 'returning') return raw;
  return 'lost';
}

function formatInTimeZone(value: string | null, timezone: string, locale: string): string {
  if (value === null) return EM_DASH;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' };
  if (timezone.length > 0) {
    try {
      return new Intl.DateTimeFormat(locale, { ...options, timeZone: timezone }).format(parsed);
    } catch {
      // Invalid timezone — format without it. Not a staleness decision.
    }
  }
  return new Intl.DateTimeFormat(locale, options).format(parsed);
}

export function MembershipMonthPage() {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const { operator } = useAuth();
  const { month = '' } = useParams<{ month: string }>();
  useDocumentTitle(
    t('reports.membership.monthTitle', 'Membership Stats — {{month}}', { month }),
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get('tab'));
  const state = TAB_STATE[tab];
  const showBandFilter = tab === 'new' || tab === 'returning';

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(PAGE_SIZE_DEFAULT);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [band, setBand] = useState('');
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildNotice, setRebuildNotice] = useState<'ok' | 'error' | null>(null);
  const [rebuildError, setRebuildError] = useState<unknown>(null);
  // Keep the last notice kind so the snackbar content does not flip to the success
  // copy while an error is transitioning out after being dismissed.
  const [lastNotice, setLastNotice] = useState<'ok' | 'error'>('ok');
  if (rebuildNotice !== null && rebuildNotice !== lastNotice) setLastNotice(rebuildNotice);
  const shownNotice = rebuildNotice ?? lastNotice;

  const header = useResource(() => fetchMembershipMonth(month), [month]);
  // The previous month feeds only the reconciliation line; a failure (or a 404) omits it.
  const prevMonth = previousMonth(month);
  const prev = useResource(
    () =>
      prevMonth === null
        ? Promise.reject(new Error('no previous month'))
        : fetchMembershipMonth(prevMonth),
    [prevMonth],
  );
  const columnsRes = useResource(
    () => fetchMembershipMonths({ from: month, to: month, page: 0, size: 1 }),
    [month],
  );
  const members = useResource(
    () =>
      fetchMembershipMembers(month, {
        state,
        band: showBandFilter ? band : '',
        search,
        sort,
        order,
        page,
        size,
      }),
    [month, state, band, showBandFilter, search, sort, order, page, size],
  );

  const canRebuild = canRebuildReports(operator?.roles);
  const durationColumns = columnsRes.data?.durationColumns ?? [];
  const timezone = header.data?.timezone ?? '';
  const neverComputed = header.status === 'ready' && header.data?.neverComputed === true;
  const unnamed = t('reports.membership.members.unnamed', 'Unnamed member');

  const handleRebuild = () => {
    setRebuilding(true);
    void rebuildReports()
      .then(() => setRebuildNotice('ok'))
      .catch((err: unknown) => {
        setRebuildError(err);
        setRebuildNotice('error');
      })
      .finally(() => setRebuilding(false));
  };

  const setTab = (next: MonthTab) => {
    setSearchParams({ tab: next });
    setPage(0);
    setBand('');
  };

  const columns = useMemo<RosterColumn<MembershipMember>[]>(
    () => [
      {
        id: 'memberName',
        header: t('reports.membership.members.customerName', 'Customer Name'),
        sortId: 'memberName',
        render: (row) => (
          <Link component={RouterLink} to={`/users/${row.sourceUserId}`}>
            {row.memberName ?? unnamed}
          </Link>
        ),
      },
      {
        id: 'latestProductName',
        header: t('reports.membership.members.product', 'Product Purchased'),
        sortId: 'latestProductName',
        render: (row) => row.latestProductName ?? EM_DASH,
      },
      {
        id: 'latestPurchaseAt',
        header: t('reports.membership.members.latestPurchase', 'Date of most recent purchase'),
        sortId: 'latestPurchaseAt',
        defaultOrder: 'desc',
        render: (row) => formatInTimeZone(row.latestPurchaseAt, timezone, i18n.language),
      },
      {
        id: 'purchaseCount',
        header: t('reports.membership.members.purchaseCount', 'Number of Purchases'),
        sortId: 'purchaseCount',
        defaultOrder: 'desc',
        render: (row) => (
          <Box component="span" sx={{ display: 'block', textAlign: 'right' }}>
            {row.purchaseCount}
          </Box>
        ),
      },
    ],
    [i18n.language, t, timezone, unnamed],
  );

  const lost = header.data?.lost ?? 0;
  const existing = header.data?.existing ?? 0;
  const newCount = header.data?.new ?? 0;
  const returning = header.data?.returning ?? 0;
  const lapsed = header.data?.lapsed ?? 0;

  const prevData = prev.status === 'ready' ? prev.data : null;
  // useResource keeps the last data while a new month loads, so pair only details that belong
  // to the month on screen.
  const showReconcile =
    header.status === 'ready' &&
    header.data !== null &&
    header.data.month === month &&
    !neverComputed &&
    prevData !== null &&
    prevData.month === prevMonth &&
    !prevData.neverComputed;
  const isCurrentMonth = month === currentMonthKey(timezone);

  return (
    <Box>
      <Typography variant="h1" sx={{ mb: 2.5 }}>
        {t('reports.membership.monthTitle', 'Membership Stats — {{month}}', { month })}
      </Typography>

      {header.data ? (
        <ReportFreshness
          asOf={header.data.asOf}
          stale={header.data.stale}
          neverComputed={header.data.neverComputed}
          timezone={header.data.timezone}
          canRebuild={canRebuild}
          onRebuild={handleRebuild}
          rebuilding={rebuilding}
        />
      ) : null}

      <Tabs
        value={tab}
        onChange={(_event, value: MonthTab) => setTab(value)}
        sx={{ mb: showReconcile ? 1.5 : 3 }}
      >
        <Tab
          value="lost"
          label={t('reports.membership.tabs.lost', 'Lost Members ({{n}})', { n: lost })}
        />
        <Tab
          value="lapsed"
          label={t('reports.membership.tabs.lapsed', 'Lapsed ({{n}})', { n: lapsed })}
        />
        <Tab
          value="existing"
          label={t('reports.membership.tabs.existing', 'Existing ({{n}})', { n: existing })}
        />
        <Tab
          value="new"
          label={t('reports.membership.tabs.new', 'New ({{n}})', { n: newCount })}
        />
        <Tab
          value="returning"
          label={t('reports.membership.tabs.returning', 'Returning ({{n}})', { n: returning })}
        />
      </Tabs>

      {showReconcile && header.data && prevData ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t(
            'reports.membership.reconcile',
            'Last month {{prevCovered}} (+{{prevLapsed}} lapsed) + {{new}} new + {{returning}} returning − {{lost}} lost = {{total}} ({{covered}} current + {{lapsed}} lapsed)',
            {
              prevCovered: prevData.coveredMembers,
              prevLapsed: prevData.lapsed,
              new: header.data.new,
              returning: header.data.returning,
              lost: header.data.lost,
              // The one computed number on this page — it explains the two published fields.
              total: header.data.coveredMembers + header.data.lapsed,
              covered: header.data.coveredMembers,
              lapsed: header.data.lapsed,
            },
          )}
          {isCurrentMonth
            ? ` ${t(
                'reports.membership.monthToDate',
                '(month to date — changes daily as grace periods end)',
              )}`
            : null}
        </Typography>
      ) : null}

      {neverComputed ? null : (
        <RosterTable
          title={t('reports.membership.members.title', 'Members')}
          columns={columns}
          rows={members.data?.content ?? []}
          rowId={(row) => String(row.sourceUserId)}
          status={members.status}
          error={members.error}
          totalElements={members.data?.totalElements ?? 0}
          page={page}
          size={size}
          sort={sort}
          order={order}
          onPageChange={setPage}
          onRowsPerPageChange={(next) => {
            setSize(next);
            setPage(0);
          }}
          onSortChange={(id, nextOrder) => {
            if (!MEMBER_SORT_ALLOWLIST.has(id)) return;
            setSort(id);
            setOrder(nextOrder);
            setPage(0);
          }}
          onApply={(query) => {
            setSearch(query.search);
            setPage(0);
          }}
          onRetry={members.refetch}
          toolbarExtra={
            showBandFilter ? (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="membership-band-filter-label">
                  {t('reports.membership.bandFilter', 'Duration')}
                </InputLabel>
                <Select
                  labelId="membership-band-filter-label"
                  label={t('reports.membership.bandFilter', 'Duration')}
                  value={band}
                  onChange={(event) => {
                    setBand(event.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">
                    {t('reports.membership.bandAll', 'All')}
                  </MenuItem>
                  {durationColumns.map((column) => (
                    <MenuItem key={column.bandKey} value={column.bandKey}>
                      {column.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null
          }
        />
      )}

      <Paper variant="outlined" sx={{ mt: 3, p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
          {t('reports.membership.legend.title', 'How to read this report')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t(
            'reports.membership.legend.lost',
            'Lost Members — members whose grace period ended this month without renewing.',
          )}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t(
            'reports.membership.legend.lapsed',
            'Lapsed (in grace) — past their expiry date but still inside the grace period; they may still renew.',
          )}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t(
            'reports.membership.legend.netMembers',
            'Net Members — members whose membership is current on the last day of the month (today, for this month).',
          )}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t(
            'reports.membership.legend.netNew',
            'Net New Members — new and returning members for the month, as sent by the server.',
          )}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t(
            'reports.membership.legend.existing',
            'Existing Members — members covered this month who are neither New nor Returning.',
          )}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t(
            'reports.membership.legend.bands',
            'Duration columns count New / Returning members in that month, by the length of the membership they bought.',
          )}
        </Typography>
        <Typography variant="body2">
          {t(
            'reports.membership.legend.scope',
            'This report describes membership during the selected month. The Users page’s membershipStatus describes it today. A member whose plan expired on the 10th is correctly Existing here and correctly Grace or Lost there.',
          )}
        </Typography>
      </Paper>

      <Snackbar
        open={rebuildNotice !== null}
        autoHideDuration={rebuildNotice === 'error' ? null : 6000}
        onClose={(_event, reason) => {
          // A failure stays until dismissed — a click elsewhere must not hide it.
          if (reason === 'clickaway' && rebuildNotice === 'error') return;
          setRebuildNotice(null);
        }}
      >
        <Alert
          severity={shownNotice === 'error' ? 'error' : 'success'}
          onClose={() => setRebuildNotice(null)}
        >
          {shownNotice === 'error' ? (
            <>
              {t('reports.freshness.rebuildError', 'Could not start a rebuild.')}
              <ErrorDetails
                error={rebuildError}
                headline={t('reports.freshness.rebuildError', 'Could not start a rebuild.')}
              />
            </>
          ) : (
            t(
              'reports.freshness.rebuildQueued',
              'Rebuild queued. Numbers will refresh when the rollup finishes.',
            )
          )}
        </Alert>
      </Snackbar>
    </Box>
  );
}
